import { normalizeUsCanadaPhone } from "@/lib/phone";

export const HUBSPOT_PORTAL_ID = (
  import.meta.env.VITE_HUBSPOT_PORTAL_ID as string | undefined
)?.trim();

export const HUBSPOT_FORM_GUID = (
  import.meta.env.VITE_HUBSPOT_FORM_GUID as string | undefined
)?.trim();

/** HubSpot data center from the embed `data-region` (e.g. na3). */
export const HUBSPOT_REGION = (
  (import.meta.env.VITE_HUBSPOT_REGION as string | undefined)?.trim() || "na1"
).toLowerCase();

export type HubSpotContactFields = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  message: string;
};

export function isHubSpotConfigured(): boolean {
  return Boolean(HUBSPOT_PORTAL_ID && HUBSPOT_FORM_GUID);
}

function hubSpotFormsApiHost(region: string): string {
  // Default / legacy NA uses api.hsforms.com; regional DCs use api-{region}.hsforms.com
  if (!region || region === "na1") return "api.hsforms.com";
  return `api-${region}.hsforms.com`;
}

/**
 * Client-side HubSpot Forms API submit (same pattern as revolin-brand-blueprint).
 * Portal ID + form GUID are public form identifiers, not private API tokens.
 */
export async function submitHubSpotContact(fields: HubSpotContactFields): Promise<void> {
  if (!HUBSPOT_PORTAL_ID || !HUBSPOT_FORM_GUID) {
    throw new Error("HubSpot is not configured");
  }

  const phone = fields.phoneNumber?.trim();
  const payload = {
    fields: [
      { name: "firstname", value: fields.firstName.trim() },
      { name: "lastname", value: fields.lastName.trim() },
      { name: "email", value: fields.email.trim() },
      ...(phone ? [{ name: "phone", value: normalizeUsCanadaPhone(phone) }] : []),
      { name: "message", value: fields.message.trim() },
    ],
    context: {
      pageUri: typeof window !== "undefined" ? window.location.href : "",
      pageName: typeof document !== "undefined" ? document.title : "",
    },
  };

  const host = hubSpotFormsApiHost(HUBSPOT_REGION);
  const res = await fetch(
    `https://${host}/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );

  if (!res.ok) {
    throw new Error(`HubSpot submit failed (${res.status})`);
  }
}
